import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbarContext } from '@monorepo/nova-ui';
import { AnalyticsEventTypes, AssetClass } from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import { useStore } from '@hub/helpers/use-store';
import gtmService from '@hub/services/analytics/analytics';

export interface UseOnboardingAssetsModalStateProps {
  open: boolean;
  onClose: () => void;
  onContinue: (selectedAsset: AssetClass[]) => void;
}

/**
 * Hook managing state and behavior for the onboarding assets selection modal.
 *
 * It tracks the currently selected asset option, handles selection changes,
 * persistence of the preference, analytics events, and dialog close behavior.
 */
export const useOnboardingAssetsModalState = ({
  open,
  onClose,
  onContinue,
}: UseOnboardingAssetsModalStateProps) => {
  const { t } = useTranslation('onboarding');
  const { showSnackbar } = useSnackbarContext();
  const {
    dataStore: { userStore },
  } = useStore();

  const [selectedAsset, setSelectedAsset] = useState<
    (AssetClass | 'both')[] | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);

  const convertSelectedToAssetClass = (
    selected: (AssetClass | 'both')[],
  ): AssetClass[] => {
    if (selected.includes('both')) {
      return [AssetClass.Forex, AssetClass.Futures];
    }
    return [selected] as unknown as AssetClass[];
  };

  useEffect(() => {
    if (open) {
      setSelectedAsset(null);
      gtmService.sendEvent({
        event: AnalyticsEventTypes.AssetSelectionViewed,
        params: {},
      });
    }
  }, [open]);

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: (AssetClass | 'both')[] | null,
  ) => {
    if (newValue !== null) {
      setSelectedAsset(newValue);
      const assetClass = convertSelectedToAssetClass(newValue);

      gtmService.sendEvent({
        event: AnalyticsEventTypes.AssetSelectionSelected,
        params: {
          value: assetClass,
        },
      });
    }
  };

  const handleContinue = async () => {
    if (!selectedAsset || isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      const {
        apiFactory: { userService },
      } = getEnv();
      const assetClass = convertSelectedToAssetClass(selectedAsset);
      await userService.updateOnboardingPreference(assetClass);

      await userStore.getUser();

      gtmService.sendEvent({
        event: AnalyticsEventTypes.AssetSelectionCompleted,
        params: {
          value: assetClass,
        },
      });

      showSnackbar({
        message: t('assets_modal.success_message', {
          defaultValue: 'Preference saved successfully',
        }),
        severity: 'success',
      });

      onContinue(assetClass);
      onClose();
    } catch (error) {
      console.error('Failed to save onboarding preference:', error);
      showSnackbar({
        message: t('assets_modal.error_message', {
          defaultValue: 'Failed to save preference. Please try again.',
        }),
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDialogClose = (
    _event: object,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return {
    selectedAsset,
    isSaving,
    onChange: handleChange,
    onContinueClick: handleContinue,
    onDialogClose: handleDialogClose,
  };
};
